import { Stack } from '@mui/material';
import { DataGrid, GridColumnVisibilityModel, GridFilterModel, GridSortModel, getGridStringOperators, useGridApiRef } from '@mui/x-data-grid';
import { frFR, enUS } from '@mui/x-data-grid/locales';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getConsultationColumns, getResponsiveColumnVisibilityModel } from './ConsultationColumns';
import { clearConsultationHighlight, openConsultationReminderModal } from 'FRONTEND/reducers/consultationSlice';
import { updateTableContext } from 'FRONTEND/reducers/tableSlice';
import { RootState } from 'FRONTEND/reducers/types';
import { useEditConsultationRemindersMutation, useSearchConsultationsQuery } from 'FRONTEND/utils/backendApi/Consultations/consultationsApi';
import useDebounced from 'FRONTEND/utils/hooks/useDebounce';
import useSize from 'FRONTEND/utils/hooks/useSize';

type ConsultationTableProps = {
  config: string;
  isCCC: boolean;
  hasMultiProjects: boolean;
  hasMultiWorkflows: boolean;
  hasMultiTenants: boolean;
}

const gridLocaleMap = {
  fr: frFR.components.MuiDataGrid.defaultProps.localeText,
  en: enUS.components.MuiDataGrid.defaultProps.localeText,
};

const DEFAULTS = {
  PAGE: 0, // Default to first page - Possibly Redux+localStorage later for persistence.
  LIMIT: 50, // Default to 50 items per page - Same as above.
  // Supported operators: 'contains' (partial match), 'equals' (exact match), 'doesNotEqual' (exclusive not equals), 'isAnyOf' (exclusive multiple values).
  // See: https://mui.com/x/react-data-grid/filtering/#server-side-filtering or console.log(getGridStringOperators()) for all available operators.
  // These are applied to most filterable string columns. For special columns individual handling, see station_vitals column implementation in ConsultationColumns.tsx.
  FILTER_OPERATORS: getGridStringOperators().filter(op => ['contains', 'equals', 'doesNotEqual', 'isAnyOf'].includes(op.value)),
};

const ConsultationTable = ({ config, isCCC, hasMultiProjects, hasMultiWorkflows, hasMultiTenants }: ConsultationTableProps) => {
  const dispatch = useDispatch();
  const location = window.location.pathname; // Used as a unique table name for storing filters/search in Redux state.
  const currentTableContext = useSelector((state: RootState) => state.table.tableContext[location] || {});

  const locale = useSelector((state: RootState) => state.user.locale);
  const userTimezone = useSelector((state: RootState) => state.user.timezone);

  const isMSFConfig = config === 'msf';

  // Search + Filters state.
  const searchQuery = useSelector((state: RootState) => state.table.tableSearchQuery[location] || '');
  const isSearchDebouncing = useSelector((state: RootState) => state.table.isSearchDebouncing);
  const filters = useSelector((state: RootState) => state.table.tableFilters[location] || []);

  const highlightedConsultationId = useSelector((state: RootState) => state.consultation.highlightedConsultationId);

  // Pagination + sorting state.
  const [currentPage, setCurrentPage] = useState(currentTableContext?.page || 0);
  const [itemPerPage, setItemPerPage] = useState(currentTableContext?.limit || 50);
  const [sortModel, setSortModel] = useState<GridSortModel>(currentTableContext?.sortModel || []);
  const [columnFilterModel, setColumnFilterModel] = useState<GridFilterModel>(currentTableContext?.columnFilterModel || { items: [] });
  const [debouncedColumnFilterModel, isColumnFilterDebouncing] = useDebounced(columnFilterModel, 300);
  const { data, isLoading: isConsultationsLoading, refetch } = useSearchConsultationsQuery({
    search: searchQuery,
    pageOffset: currentPage * itemPerPage,
    limit: itemPerPage,
    filters,
    sortModel,
    columnFilterModel: debouncedColumnFilterModel,
  });
  const [_editConsultationReminders, { isLoading: isEditConsultationLoading }] = useEditConsultationRemindersMutation();

  // Refetch when a consultation is selected, allowing update of "unread" status.
  useEffect(() => {
    refetch();
  }, [currentTableContext.previouslySelectedConsultationId, refetch]);

  // Columns and Rows management.
  const columns = getConsultationColumns({
    config,
    isCCC,
    hasMultiProjects,
    hasMultiWorkflows,
    hasMultiTenants,
    userTimezone,
    onOpenReminderModal: (id) => dispatch(openConsultationReminderModal(id)),
  }).map(col => col.filterable === false || col.filterOperators != null ? col : { ...col, filterOperators: DEFAULTS.FILTER_OPERATORS }); // Add allowed filter operators to filterable columns.
  const mainContainer = document.querySelector('#react_consultationTable');
  const changingWidth = useSize({ current: mainContainer });

  // User's manual show/hide overrides, stored as deltas from the responsive model.
  const [userVisibilityOverrides, setUserVisibilityOverrides] = useState<GridColumnVisibilityModel>(currentTableContext?.columnVisibilityModel || {});
  const responsiveVisibilityModel = getResponsiveColumnVisibilityModel({ columns, currentSize: changingWidth || 0 });
  // Merge: responsive model is the base; user overrides take precedence.
  const columnVisibilityModel = { ...responsiveVisibilityModel, ...userVisibilityOverrides };

  const handleColumnVisibilityModelChange = (newModel: GridColumnVisibilityModel) => {
    // Persist only fields the user has explicitly toggled away from the responsive default.
    const overrides: GridColumnVisibilityModel = {};
    for (const [field, visible] of Object.entries(newModel)) {
      if (visible !== (responsiveVisibilityModel[field] ?? true)) {
        overrides[field] = visible;
      }
    }
    setUserVisibilityOverrides(overrides);
  };

  const rows = data?.consultations || [];
  const totalFilteredRows = data?.recordsFiltered || 0; // Total number of rows matching the search criteria, used for pagination

  // When a new consultation arrives via websocket, go to page 0, refetch, then clear the highlight after 5 seconds.
  useEffect(() => {
    if (!highlightedConsultationId) {
      return;
    }
    setCurrentPage(0);
    refetch();
    const timer = setTimeout(() => {
      dispatch(clearConsultationHighlight());
    }, 5000);
    return () => clearTimeout(timer);
  }, [highlightedConsultationId]);

  // Update state when context changes.
  const updateContext = () => dispatch(updateTableContext({
    tableName: location,
    contextObject: {
      page: currentPage,
      limit: itemPerPage,
      sortModel,
      columnFilterModel,
      columnVisibilityModel: Object.keys(userVisibilityOverrides).length > 0 ? columnVisibilityModel : null, // Only persist visibility if user has made overrides, otherwise rely on responsive defaults.
    },
  }));
  useEffect(() => {
    updateContext();
  }, [currentPage, itemPerPage, sortModel, columnFilterModel, userVisibilityOverrides]);

  // Conditional CSS on row based on consultation props.
  const addCustomClassesToRow = (params) => {
    const classes = [];
    if (params.row.unread) {
      classes.push('row-unread');
    }
    if (params.row.is_draft) {
      classes.push('row-draft');
    }
    if (highlightedConsultationId && params.row.id === highlightedConsultationId) {
      classes.push('row-new-consultation');
    }
    if (currentTableContext.previouslySelectedConsultationId === params.row.id) {
      classes.push('row-previously-selected');
    }
    return classes.join(' ');
  };

  // Auto-scroll to previously selected consultation when re-visiting the table after having opened a consultation.
  // Virtualization is disabled (see disableVirtualization prop) so all rows are always in the DOM,
  // meaning getRowElement is always reliable and no deferred tricks are needed.
  // scrolledForIdRef prevents re-scrolling on subsequent resizes, so it only triggers once per targeted consultation ID.
  const apiRef = useGridApiRef();
  const scrolledForIdRef = useRef<string | null>(null);
  const handleGridResize = () => {
    const targetId = currentTableContext.previouslySelectedConsultationId;
    if (!targetId || scrolledForIdRef.current === targetId) {
      return;
    }
    if (rows.findIndex(r => r.id === targetId) === -1) {
      return;
    }
    scrolledForIdRef.current = targetId;
    apiRef.current.getRowElement(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <Stack sx={{ height: '100%' }}>
      {/* height: 100% fills .main-content__container, giving DataGrid a bounded height so it manages its own scroll and sticky column headers work natively */}
      <DataGrid
        apiRef={apiRef}
        // Data
        rows={rows}
        columns={columns}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
        localeText={gridLocaleMap[locale] || gridLocaleMap.en}
        paginationMode='server'
        sortingMode='server'
        filterMode='server'
        // Since we have a max of 50 items per page and it's server-sided pagination, we can afford to disable virtualisation.
        // This allows for cleaner programatic scrolling when navigating back to the table.
        disableVirtualization
        onResize={handleGridResize}
        loading={isConsultationsLoading || isEditConsultationLoading || isSearchDebouncing || isColumnFilterDebouncing}
        rowCount={totalFilteredRows}
        paginationModel={{ page: currentPage, pageSize: itemPerPage }}
        onPaginationModelChange={(model) => {
          setCurrentPage(model.page);
          setItemPerPage(model.pageSize);
        }}
        sortModel={sortModel}
        onSortModelChange={(model) => setSortModel(model as GridSortModel)}
        filterModel={columnFilterModel}
        onFilterModelChange={(model) => setColumnFilterModel(model as GridFilterModel)}
        // Styling
        getRowClassName={addCustomClassesToRow}
        getRowHeight={() => 'auto'}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
            maxHeight: '70px !important',
            transition: 'background-color 1.5s ease',
          },
          '& .MuiDataGrid-row:nth-of-type(even):not(.row-draft):not(.row-new-consultation)': {
            backgroundColor: 'rgba(0, 0, 0, 0.03)',
          },
          '& .MuiDataGrid-row:hover': {
            color: (theme) => theme.palette.primary.main,
            '& .row-chip': {
              backgroundColor: (theme) => theme.palette.primary.main,
              color: (theme) => theme.palette.common.white,
            },
            '& .row-avatar': {
              borderColor: (theme) => theme.palette.primary.main,
            },
          },
          '& .MuiDataGrid-cell': {
            padding: '4px',
            whiteSpace: 'normal',
            overflow: 'hidden',
            alignContent: 'flex-start',
          },
          '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus': {
            outline: 'none',
          },
          // Fix pagination controls vertical alignment: Bootstrap sets margin-bottom: 1rem on all <p> tags,
          // which offsets the "Lignes par page" and "1–N sur N" labels inside the flex toolbar.
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { margin: 0 },
          // In MSF config, a floating button overlaps the right side of the pagination bar, so we shift them a little.
          '& .MuiDataGrid-footerContainer': { ...(isMSFConfig && { paddingRight: '80px' }) },
          // Custom classes for conditional row styling. See "addCustomClassesToRow" function above.
          '& .row-unread': { fontWeight: '700' },
          '& .row-draft': { backgroundColor: (theme) => theme.palette.grey[400] },
          '& .row-new-consultation': { backgroundColor: '#d6eaf8', transition: 'background-color 1.5s ease' },
          '& .row-previously-selected': { border: (theme) => `2px solid ${theme.palette.primary.main}` },
        }}
      />
    </Stack>
  );
};

export default ConsultationTable;
